import React, { useState } from 'react'
import { Card, CardHeader, CardTitle, Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@duck/ui'
import { usePersons } from '@/hooks/usePersons'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'
import { SendAssignment } from '../Assignments/SendAssignment'
import { StandardLoader } from '@/components/StandardLoader'
import { toast } from 'sonner'
import { CohortPeopleTable } from './CohortPeopleTable'
import { CohortPersonDetails } from '../cohortPageData'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { UserGroupIcon } from '@heroicons/react/24/outline'
import { CohortPeopleListSkeleton } from './CohortPeopleListSkeleton'

interface CohortPeopleProps {
  listTitle: string
  peopleDetails: CohortPersonDetails[]
  cohortId: string
  handleCohortDataChange?: () => void
  listMode?: 'all' | 'task'
  skeleton?: boolean
  onAddPeople?: () => void
}

export const CohortPeopleList: React.FC<CohortPeopleProps> = ({ listTitle, peopleDetails, cohortId, handleCohortDataChange, listMode = 'all', skeleton = false, onAddPeople }) => {
  const personIds = peopleDetails.map(person => person.personId)
  const { persons, isLoading, isError, error } = usePersons(personIds)
  const { t } = useTar()
  const { t: tP } = usePartnerTar()
  const { getOrgSetting, isLoading: isOrganizationLoading } = useOrganizationSettings()
  const [isSendAssignmentDialogOpen, setIsSendAssignmentDialogOpen] = useState(false)
  const [checkedPersons, setCheckedPersons] = useState<Set<string>>(new Set())

  const handleSendAssignment = () => {
    if (checkedPersons.size === 0) {
      toast.error(tP('pleaseSelectPeople'))
      return
    }
    setIsSendAssignmentDialogOpen(true)
  }

  const CardWrapper = ({ children, headerAction }: { children: React.ReactNode; headerAction?: React.ReactNode }) => {
    return (
      <Card>
        <CardHeader className={headerAction ? "flex-row items-center justify-between" : ""}>
          <CardTitle>{listTitle}</CardTitle>
          {headerAction}
        </CardHeader>
        {children}
      </Card>
    )
  }

  if (skeleton) {
    return <CohortPeopleListSkeleton listTitle={listTitle} />
  }

  if (isError || error) {
    return (
      <CardWrapper>
        <div className="text-center py-8">
          <p className="text-muted-foreground">{error ? t('common.errorLoading', { item: tP('people').toLowerCase() }) : t('common.error')}</p>
        </div>
      </CardWrapper>
    )
  }

  if (isLoading || isOrganizationLoading) {
    return (
      <CardWrapper>
        <div className="py-8">
          <StandardLoader />
        </div>
      </CardWrapper>
    )
  }

  if (!personIds || personIds.length === 0) {
    return (
      <CardWrapper>
        <NoDataEmptyState
          icon={UserGroupIcon}
          title={tP('noPeopleYet')}
          description={tP('noPeopleDescription')}
          buttonText={listMode === 'all' ? tP('addPeople') : undefined}
          onButtonClick={listMode === 'all' ? onAddPeople : undefined}
        />
      </CardWrapper>
    )
  }

  if (listMode === 'task') {
    return (
      <CardWrapper>
        <CohortPeopleTable
          cohortId={cohortId}
          peopleDetails={peopleDetails}
          persons={persons}
          checkedPersons={checkedPersons}
          setCheckedPersons={setCheckedPersons}
          view="task"
        />
      </CardWrapper>
    )
  }

  const invitationEmails = getOrgSetting('invitationEmails') ?? false

  return (
    <CardWrapper
      headerAction={
        <Button onClick={handleSendAssignment}>{invitationEmails ? t('partner.sendAssignment') : t('partner.newAssignment')}</Button>
      }
    >
      <CohortPeopleTable
        cohortId={cohortId}
        peopleDetails={peopleDetails}
        persons={persons}
        checkedPersons={checkedPersons}
        setCheckedPersons={setCheckedPersons}
      />

      <Dialog
        open={isSendAssignmentDialogOpen}
        onOpenChange={setIsSendAssignmentDialogOpen}
      >
        <DialogContent width="xs">
          <DialogHeader>
            <DialogTitle>{invitationEmails ? t('partner.sendAssignment') : t('partner.newAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            personIds={Array.from(checkedPersons)}
            sendInvitationEmails={invitationEmails}
            onClose={() => setIsSendAssignmentDialogOpen(false)}
            onSend={() => {
              setIsSendAssignmentDialogOpen(false)
              setCheckedPersons(new Set())
              handleCohortDataChange?.()
            }}
          />
        </DialogContent>
      </Dialog>
    </CardWrapper>
  )
}
